/**
 * reviewFlywheel.js — quiet, high-converting review flywheel (PROMPT-52).
 *
 * ONE shared module for both the web app and the Chrome extension side panel
 * (same React build). It captures sentiment + an optional comment in-product,
 * then routes 4–5★ users to the Chrome Web Store reviews page and 1–3★ users to
 * a private feedback box. Storage adapter + env detection make it context-safe:
 * the web build never throws on `chrome` being undefined, and the extension
 * build never relies on a published `tabs` permission (we use window.open).
 *
 * The component (<ReviewPrompt/>) renders on the `persona:review-show` event
 * that recordUse() dispatches once the gating conditions are met.
 */
import { track as analyticsTrack } from './analytics.js';
import { apiFetch } from './supabase.js';

export const RF = {
  STORAGE_KEY: 'persona_review_state_v1',
  CWS_REVIEWS_URL: 'https://chromewebstore.google.com/detail/persona/oppecdkehgmdjcnlkohlombfdlfjfeff/reviews',
  MIN_USES_BEFORE_FIRST_ASK: 3,   // 3rd successful *use* (insert/copy), not generation
  REARM_DAYS: 7,                  // re-ask only after BOTH conditions
  REARM_USES: 8,
  MAX_ASKS: 3,                    // lifetime cap
  NEGATIVE_SUPPRESS_DAYS: 90,
  POSITIVE_THRESHOLD: 4,          // stars >= 4 => happy branch
  SHOW_DELAY_MS: 1500,            // appear a beat after the success moment
};

export const SHOW_EVENT = 'persona:review-show';

// Env detection — mirrors the app-wide convention (navigate.js / AIMail).
export const IS_EXTENSION =
  typeof window !== 'undefined' &&
  (window.__AIMAIL_IS_EXTENSION ||
    window.__AIMAIL_FORCE_COMPACT ||
    (typeof chrome !== 'undefined' && !!(chrome.runtime && chrome.runtime.id)));

const hasChromeStorage =
  typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local;

const DEFAULT = {
  uses: 0,
  daysUsed: [],            // ['2026-06-25', ...] deduped, cap last 14
  asksShown: 0,
  lastAskAt: null,         // ISO string
  usesAtLastAsk: 0,
  terminalPositive: false, // clicked store OR saved testimonial -> never ask again
  gaveFeedbackAt: null,    // negative path -> suppress NEGATIVE_SUPPRESS_DAYS
  optedOut: false,         // explicit dismiss escalation
  variant: null,           // A/B bucket, assigned once on first ask (see pickVariant)
};

// ─── A/B assignment (L2 deliverable 1) ───────────────────────────────────────

/** The four review-ask variants. Order is the bucket order; do not reorder. */
export const VARIANT_IDS = ['direct', 'early', 'founder', 'specific'];

/**
 * FNV-1a. A hash, not a CSPRNG — we need "evenly spread and identical on every
 * device for the same id", which this gives in a few lines with no dependency.
 */
function hashStr(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Resolve this device's variant, assigning it once and remembering it.
 *
 * Bucketed on persona_client_id — the same stable per-device id analytics.js
 * mints — so a user sees one variant forever rather than a fresh roll per
 * session, which would smear the two arms together and make the test unreadable.
 *
 * The result is persisted into the flywheel's own state rather than recomputed,
 * because the client id can legitimately be regenerated (cleared storage, a new
 * profile). Persisting means a mid-experiment reset doesn't silently move
 * someone between arms — it just keeps whatever they were first shown.
 */
export function pickVariant(clientId) {
  const id = clientId || 'anon';
  return VARIANT_IDS[hashStr(id) % VARIANT_IDS.length];
}

/**
 * The device id to bucket on. analytics.js mints persona_client_id in
 * localStorage in BOTH builds (the extension panel is a normal page), and by the
 * time the ask fires — the 3rd use — panel_opened has long since created it.
 *
 * The fallback is deliberately random rather than a constant: hashing a fixed
 * string would drop every id-less device into the same arm and quietly skew the
 * whole experiment. The chosen variant is persisted immediately after, so the
 * randomness is used once and never re-rolled.
 */
function bucketKey() {
  try {
    const id = localStorage.getItem('persona_client_id');
    if (id) return id;
  } catch { /* storage blocked — fall through */ }
  return `anon-${Math.random().toString(36).slice(2)}`;
}

/** Variant for this device, assigned on first call and persisted thereafter. */
export async function resolveVariant() {
  try {
    const s = await loadState();
    if (s.variant && VARIANT_IDS.includes(s.variant)) return s.variant;
    const v = pickVariant(bucketKey());
    s.variant = v;
    await saveState(s);
    return v;
  } catch {
    // Storage unavailable — still return a deterministic arm so the card renders.
    return pickVariant(bucketKey());
  }
}

// ─── Storage adapter (async, uniform) ────────────────────────────────────────

export async function loadState() {
  try {
    if (hasChromeStorage) {
      const o = await chrome.storage.local.get(RF.STORAGE_KEY);
      return { ...DEFAULT, ...(o[RF.STORAGE_KEY] || {}) };
    }
    const raw = localStorage.getItem(RF.STORAGE_KEY);
    return { ...DEFAULT, ...(raw ? JSON.parse(raw) : {}) };
  } catch {
    return { ...DEFAULT };
  }
}

export async function saveState(s) {
  try {
    if (hasChromeStorage) return chrome.storage.local.set({ [RF.STORAGE_KEY]: s });
    localStorage.setItem(RF.STORAGE_KEY, JSON.stringify(s));
  } catch { /* storage unavailable — silently skip */ }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysSince = (iso) =>
  iso ? (Date.now() - new Date(iso).getTime()) / 86400000 : Infinity;

// ─── Eligibility ──────────────────────────────────────────────────────────────

export function eligible(s) {
  if (s.terminalPositive || s.optedOut) return false;
  if (s.asksShown >= RF.MAX_ASKS) return false;
  if (s.uses < RF.MIN_USES_BEFORE_FIRST_ASK) return false;
  if (s.gaveFeedbackAt && daysSince(s.gaveFeedbackAt) < RF.NEGATIVE_SUPPRESS_DAYS) return false;
  if (s.asksShown === 0) return true; // first ask: threshold met
  return daysSince(s.lastAskAt) >= RF.REARM_DAYS          // subsequent: BOTH conditions
      && (s.uses - s.usesAtLastAsk) >= RF.REARM_USES;
}

// ─── Trigger — call from the realized-value (USE) moment, on success only ──────

let shownThisSession = false; // in-memory: show at most once per session

export async function recordUse() {
  try {
    const s = await loadState();
    s.uses += 1;
    const t = todayStr();
    if (!s.daysUsed.includes(t)) s.daysUsed = [...s.daysUsed, t].slice(-14);
    await saveState(s);
    if (!shownThisSession && eligible(s)) {
      shownThisSession = true;
      setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent(SHOW_EVENT)); } catch { /* ignore */ }
      }, RF.SHOW_DELAY_MS);
    }
  } catch { /* never throw from the success path */ }
}

// ─── Terminal / lifecycle transitions (persist immediately) ──────────────────

export async function onShow(variant) {
  const s = await loadState();
  s.asksShown += 1;
  s.lastAskAt = new Date().toISOString();
  s.usesAtLastAsk = s.uses;
  await saveState(s);
  // The variant rides on the DENOMINATOR as well as the click. Without it here
  // there is no per-arm impression count, and prompt->review conversion cannot
  // be computed for any arm — the whole A/B would be unreadable.
  track('review_prompt_shown', { source: source(), variant: variant || s.variant || 'unknown' });
}

export async function markTerminalPositive() {
  const s = await loadState();
  s.terminalPositive = true;
  await saveState(s);
}

export async function markGaveFeedback() {
  const s = await loadState();
  s.gaveFeedbackAt = new Date().toISOString();
  await saveState(s);
}

/** Optional opt-out escalation after repeated dismisses with no rating. */
export async function noteDismiss() {
  const s = await loadState();
  s._dismisses = (s._dismisses || 0) + 1;
  if (s._dismisses >= 2) s.optedOut = true;
  await saveState(s);
}

// ─── Routing + clipboard (authenticity rule) ─────────────────────────────────

export const source = () => (IS_EXTENSION ? 'extension' : 'web');

// The extension manifest has no `tabs` permission, so window.open is the only
// portable way to reach the store from the side panel AND the web app.
export function openUrl(url) {
  try { window.open(url, '_blank', 'noopener,noreferrer'); } catch { /* ignore */ }
}

/**
 * Copy ONLY the user's own words (never templated text, never URLs/emails — the
 * store filters identical/linky reviews) and open the listing. Returns whether
 * a note was copied so the UI can show the right toast.
 */
export async function routeToStore(userTypedNote) {
  const note = (userTypedNote || '').trim();
  if (note) {
    try { await navigator.clipboard.writeText(note); } catch { /* clipboard blocked — open anyway */ }
  }
  openUrl(RF.CWS_REVIEWS_URL);
  return !!note;
}

// ─── Backend (best-effort; never blocks the UI, no-ops when signed out) ───────

export async function postFeedback({ kind, stars = null, comment = null, allowQuote = false }) {
  try {
    await apiFetch('/api/v1/feedback', {
      method: 'POST',
      body: JSON.stringify({
        source: source(),
        kind,                 // 'feedback' | 'testimonial' | 'review_intent'
        stars,
        comment: comment || null,
        allow_quote: !!allowQuote,
      }),
    });
  } catch { /* best-effort: anonymous users / offline — UI still completes */ }
}

// ─── Analytics (thin wrapper over the app's track; no-op if absent) ───────────

export function track(event, props = {}) {
  try { analyticsTrack(event, props); } catch { /* ignore */ }
}
